/**
 * The surface list, ZOD-FREE.
 *
 * This exists because the host-page snippet evaluates scope rules, and the
 * snippet bundle must not contain zod — `widget/build.ts` fails the build if it
 * does (bh.js is loaded on every page of every customer's site; a validation
 * library there is dead weight on someone else's page-load budget).
 *
 * So the matcher imports its runtime values from HERE, and the zod schema in
 * `./schema` imports from here too. One list, two consumers, no duplication of
 * the strings themselves.
 *
 * DRIFT FROM THE PKL VOCABULARY IS A COMPILE ERROR, both ways — see the
 * exhaustiveness check below. The Pkl module stays the source of truth; this is
 * a type-checked mirror of it, not a second opinion.
 */
import type { WidgetSurface } from "../gen/modules/widgetSurfaces.schema";

export type { WidgetSurface };

/**
 * Every surface a rule may name. Order is display order in the editor; the
 * matcher does not care.
 *
 * `satisfies` rejects a name that is NOT in the Pkl vocabulary. The check
 * below rejects a Pkl name MISSING from here. Together they make this
 * impossible to drift from without tsc complaining.
 */
export const WIDGET_SURFACES = [
  "bar",
  "voice",
  "nav_launcher",
  "hero_bar",
  "in_content_line",
  "end_of_page_banner",
  "banner",
] as const satisfies readonly WidgetSurface[];

/** Compile error if Pkl gains a surface this list has not caught up with. */
type MissingFromMirror = Exclude<WidgetSurface, (typeof WIDGET_SURFACES)[number]>;
const _exhaustive: MissingFromMirror extends never ? true : never = true;
void _exhaustive;

/**
 * "Every surface this bundle knows about."
 *
 * Deliberately not a fixed list and not in the Pkl vocabulary: a rule saying
 * "hide everything" must keep meaning that as surfaces are added, including on
 * bundles cached in the field that predate them. A rule listing surfaces by
 * name is the one that ages; a `*` rule never does.
 */
export const ALL_SURFACES = "*";

/** Every known surface, on or off. */
export type SurfaceGates = { readonly [K in WidgetSurface]: boolean };
